import './DodLayoutConditionBuilder.scss';
import React, {useMemo} from 'react';
import classnames from 'classnames';
import {DodFactSet, FactCondition} from '@/types/DodRun';

export type DodLayoutConditionBuilderProps = {
    className?: string;
    name?: string;
    value: FactCondition[];
    facts: DodFactSet[];
    onEdit?(): void;
    onClear?(): void;
    title?: string;
    mode: string | null;
    conditionMarket?: string;
    conditionMarketDisplay?: string;
    conditionTimePeriod?: string;
    conditionTimePeriodDisplay?: string;
} & Partial<Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>>;

const COMPARATOR_MAP = {
    gt:         'Greater Than (>)',
    gte:        'Greater Than Or Equal To (≥)',
    lt:         'Less Than (<)',
    lte:        'Less Than Or Equal To (≤)',
    eq:         'Equal To (=)',
    neq:        'Not Equal To (≠)',
    btw:        'Between',
    nbtw:       'Not Between',
    before:     'Before',
    after:      'After',
    between:    'Between',
};

const baseClassName = 'dod-layout-condition-builder';

export function DodLayoutConditionBuilder({
  className,
  name,
  facts,
  value,
  onEdit,
  onClear,
  title,
  mode,
  conditionMarket,
  conditionMarketDisplay,
  conditionTimePeriod,
  conditionTimePeriodDisplay,
  ...props
}: DodLayoutConditionBuilderProps) {
  const isLayoutMode = mode === 'layout';

  return (
      <div className={classnames(baseClassName, className)} {...props}>
          <header className={`${baseClassName}__header`}>
              <h1 className={`${baseClassName}__header--title`}>Conditions:</h1>
              <div className={`${baseClassName}accordion-wizard-step__actions`}>
                  <button
                      className={classnames(`${baseClassName}__action`, `${baseClassName}__action--edit`)}
                      onClick={onEdit}
                      disabled={isLayoutMode}
                  />
                  {(Boolean(value?.length) || Boolean(name)) && (
                      <button
                          className={classnames(`${baseClassName}__action`, `${baseClassName}__action--delete`)}
                          onClick={onClear}
                          disabled={isLayoutMode}
                      />
                  )}
              </div>
          </header>
          <DodConditionSummary className={`${baseClassName}__summary`} conditions={value} conditionMarketDisplay={conditionMarketDisplay ?? conditionMarket} conditionTimePeriodDisplay={conditionTimePeriodDisplay ?? conditionTimePeriod} name={name} />
      </div>
  );
}

interface DodConditionSummaryProps {
    className?: string;
    conditions: FactCondition[];
    facts?: DodFactSet[];
    conditionMarketDisplay?: string;
    conditionTimePeriodDisplay?: string;
    name?: string;
}

function DodConditionSummary({conditions, conditionMarketDisplay, conditionTimePeriodDisplay, className, name}: DodConditionSummaryProps) {

    const fmtValue = (v: string, fact: string): string => {
        if (!v) return '';
        return fact.includes('$') && !fact.includes('% Change vs Year-Ago')
            ? `$${v.replace(/^\$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
            : v.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const summary = useMemo<string>(() => {

        if (name) {
            return name;
        }

        if (!conditions.length) {
            return `You do not have any conditions applied.`
        }

        let summary: string = '';
        conditions.forEach((condition, index) => {
            if (index > 0) {
                summary += ` ${condition.condition.toLocaleUpperCase()} `;
            }
            summary += ` "${condition.fact}" `;
            summary += ' is ';
            summary += ` ${COMPARATOR_MAP[condition.comparator]} `;
            //BYZ-12119 - adding .replace method to format condition.value to display thousand separator
            if (Array.isArray(condition.value)) {
                summary += `${fmtValue(condition.value[0], condition.fact)} and ${fmtValue(condition.value[1], condition.fact)}`;
            } else {
                summary += fmtValue(condition.value, condition.fact);
            }
        });

        const marketText = (!conditionMarketDisplay || conditionMarketDisplay === 'each_market')
            ? 'each market'
            : conditionMarketDisplay;

        const timePeriodText = (!conditionTimePeriodDisplay || conditionTimePeriodDisplay === 'each_time_period')
            ? 'each time period'
            : conditionTimePeriodDisplay;

        return `All products where ${summary} based on ${marketText} and ${timePeriodText}.`;
    }, [conditions, conditionMarketDisplay, conditionTimePeriodDisplay, name])

    return <div className={className}>
        {summary}
    </div>
}

export default DodLayoutConditionBuilder;
